import { useId, useMemo, useState } from 'react'
import { scaleLinear, scaleTime } from 'd3-scale'
import { fixedDecimals } from '../../platform/fixedDecimals'
import { Money } from '../../platform/primitives/Money'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import '../../platform/notes.css'
import type { Account } from './useAccounts'
import './accounts.css'

/**
 * Fixture-backed balance history — Ticket 2 (#396), Step 3.
 *
 * **No endpoint exists.** `GET /accounts/{id}/balance-history?range=` is
 * proposed in the M5 spec's Backend Gaps table and does not exist. This
 * component is the whole of what "fixture-backed" means here: a
 * deterministic synthetic series, seeded per account and range, ending at
 * the account's REAL current balance so it never contradicts the hero above
 * it.
 *
 * **Why it stays a fixture even once that endpoint ships:** backend#355 (the
 * snapshot table) is the blocker. `current_balance` is overwritten in place on
 * every sync, so no backfill is possible; real history only begins to accrue
 * from the moment a snapshot table exists. Until then there is nothing true to
 * draw, so this series is synthetic and labelled so.
 *
 * **First chart in this app.** Hand-rolled SVG on `d3-scale` alone (rule 3)
 * — `d3-shape` is deliberately NOT a dependency: the path below is a plain
 * polyline with no curve or area fill, so nothing here would call
 * `line()`/`area()`. The pattern this file sets for M6's charts: scale math
 * from `d3-scale`, the `<path>` string built by hand, same as every other
 * primitive in this app is CSS/SVG rather than a component library.
 *
 * **Chart parts (M5 refresh, Ticket 5 / B4):** three horizontal gridlines at
 * `--line`, a 2.6px `--s1` stroke, an end dot at the series end-point ringed
 * in `--surface`, and month tick labels beneath with the current month bold
 * in `--accent-ink`. No value scale and no legend. Below 640px (M5-R30) the
 * sparkline keeps the line, the end dot and the one callout, and drops the
 * gridlines and ticks — axis chrome is what a sparkline sheds.
 *
 * **`--s1` is the series colour, a data mark, not an accent role.** In dark
 * theme it resolves to the same lime as `--accent`, and the end dot IS filled
 * with it. Rule 9 governs `--accent` as a role (primary action, hero tile,
 * selection system); `--s1` is the series-1 data encoding, and a ~12px marker
 * at the end of the series it colours is part of that encoding, so it does not
 * count against the role. Do not copy this as licence for a second lime fill
 * that is not a data mark. The gridlines and dot are decorative (inside the
 * `role="img"` svg); the month labels are real text outside it.
 */

type Range = '1M' | '6M' | '1Y'

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: '1M', label: '1M' },
  { value: '6M', label: '6M' },
  { value: '1Y', label: '1Y' },
]

/** How many days back each range spans, and the spacing between generated
 *  points — sparser for a longer span so a 1Y series is not 365 points
 *  jammed into the same width a 1M series draws 30 into. */
const RANGE_DAYS: Record<Range, number> = { '1M': 30, '6M': 182, '1Y': 365 }
const RANGE_STEP_DAYS: Record<Range, number> = { '1M': 1, '6M': 7, '1Y': 14 }

const DAY_MS = 24 * 60 * 60 * 1000

type Point = { date: Date; cents: number }

/** FNV-1a-ish string hash → a 32-bit seed, so the SAME account+range always
 *  draws the SAME series (a reshuffling chart on every re-render would read
 *  as the data itself changing). */
function seedFrom(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * mulberry32 — no dependency, deterministic per seed. A second small copy
 * rather than importing `app/texture/prng.ts`'s: that module is scoped to
 * the decorative background mesh, and reaching across into it for an
 * unrelated fixture would couple two things with no reason to change
 * together. Same tiny algorithm, same "not for anything security-adjacent"
 * caveat.
 */
function mulberry32(seed: number): () => number {
  let a = seed
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * The series for one account and range — computed fresh on every call,
 * never hoisted to module scope. A top-level `const FIXTURE = {...}` would
 * ship in the production bundle regardless of whether this component ever
 * renders (Rollup cannot prove a `.map`/loop pure and keeps the array it
 * built) — the exact defect `probe.build.test.ts` records on #329. This
 * component has no reason to be unreachable the way the dev-only probe
 * does, which is what makes the cheap guard the export-shape test below
 * rather than a full production build: the property that matters is "no
 * second, importable copy of this data", not "absent from the bundle".
 *
 * Walks BACKWARD from `currentCents` so the series always ends where the
 * account actually is — a fixture disagreeing with the hero balance above
 * it would be its own defect, worse than an obviously-synthetic one.
 */
function buildSeries(accountId: string, currentCents: number, range: Range): Point[] {
  const days = RANGE_DAYS[range]
  const step = RANGE_STEP_DAYS[range]
  const count = Math.floor(days / step) + 1
  const rand = mulberry32(seedFrom(`${accountId}:${range}`))
  // Swing floor (2000 = $20) so a near-zero balance still visibly moves —
  // 3% of a zero balance is zero, which would draw a flat line for exactly
  // the accounts where "history" is most worth seeing.
  const swing = Math.max(Math.abs(currentCents) * 0.03, 2000)

  const cents: number[] = [currentCents]
  for (let i = 1; i < count; i++) {
    cents.push(cents[i - 1]! - Math.round((rand() - 0.5) * 2 * swing))
  }
  cents.reverse()

  const now = Date.now()
  return cents.map((c, i) => ({
    date: new Date(now - (count - 1 - i) * step * DAY_MS),
    cents: c,
  }))
}

const CHART_WIDTH = 640
/**
 * N2 (architect round 2, #396, non-blocking): `CHART_HEIGHT`/`SPARK_HEIGHT`
 * read as pixel heights and are not — the svg is `width: 100%; height: auto`
 * on a `0 0 640 H` viewBox (`accounts.css`), so the RENDERED height is
 * `containerWidth × H / 640`. `160` renders ~225px tall in a 900px panel;
 * `56` renders ~30px on a 390px phone. These are aspect-ratio inputs, not
 * heights — worth stating plainly here since this is the file M6's charts
 * copy.
 */
const CHART_HEIGHT = 160
const SPARK_HEIGHT = 56
const PAD = 6

/**
 * B1 (architect review, #396) -- the whole reason this exists. `buildSeries`
 * walks a seeded PRNG backwards from the account's TRUE `currentCents`, and
 * before this constant nothing on screen or in the accessible name said the
 * result was synthetic: a user could read a twelve-month decline that never
 * happened, in their own currency, rendered in the same visual language
 * (`Money`, a solid line) this app uses for real figures. Fixture-backed is
 * the plan's own Step 3 decision and stays; what was missing is the label
 * that makes it honest. Said on BOTH channels below -- the visible note for a
 * sighted user, folded into the svg's `aria-label` for a screen-reader user --
 * because each receives the page differently and neither can infer it from
 * the other's channel.
 */
const SAMPLE_DATA_NOTE =
  "Sample data \u2014 balance history isn't available yet."

const GRIDLINE_COUNT = 3
/** The viewBox is scaled to the container (x0.45 on a 375px phone), so the
 *  sparkline's dot is drawn larger in viewBox units. It still renders smaller
 *  than the desktop dot (about 7px against 12px) — fine for a sparkline. The
 *  scales are inset by the dot's full extent (radius plus half the 2-unit
 *  ring) so the dot fits inside the viewBox and the svg needs no
 *  `overflow: visible`. */
const END_DOT_R = 4
const END_DOT_R_SPARK = 8
const RING_HALF = 1
function insetFor(height: number): number {
  const r = height === SPARK_HEIGHT ? END_DOT_R_SPARK : END_DOT_R
  return Math.max(PAD, r + RING_HALF)
}

/** One tick per calendar month the series touches, in order. The text keeps
 *  its natural casing ("Sep") so assistive tech reads a month, not "sep"; the
 *  artboard's lower-case is CSS (`text-transform`). The last is the current month. */
function monthTicks(points: Point[]): { key: string; label: string; current: boolean }[] {
  const seen = new Map<string, string>()
  for (const p of points) {
    const key = `${p.date.getFullYear()}-${p.date.getMonth()}`
    if (!seen.has(key)) {
      seen.set(key, p.date.toLocaleString('en-US', { month: 'short' }))
    }
  }
  const keys = [...seen.keys()]
  return keys.map((key, i) => ({ key, label: seen.get(key) ?? '', current: i === keys.length - 1 }))
}

function scalesFor(points: Point[], height: number) {
  const inset = insetFor(height)
  const dates = points.map((p) => p.date)
  const values = points.map((p) => p.cents)
  const x = scaleTime()
    .domain([dates[0] ?? new Date(), dates[dates.length - 1] ?? new Date()])
    .range([inset, CHART_WIDTH - inset])
  const min = Math.min(...values)
  const max = Math.max(...values)
  // A perfectly flat series (min === max) would hand d3-scale a zero-width
  // domain — pad by a unit so the line still has somewhere to go rather than
  // dividing by zero.
  const y = scaleLinear()
    .domain([min === max ? min - 1 : min, min === max ? max + 1 : max])
    .range([height - inset, inset])
  return { x, y }
}

function pathFor(points: Point[], height: number): string {
  const { x, y } = scalesFor(points, height)
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${fixedDecimals(x(p.date), 2)},${fixedDecimals(y(p.cents), 2)}`)
    .join(' ')
}

function endPointFor(points: Point[], height: number): { cx: number; cy: number } | null {
  const last = points[points.length - 1]
  if (!last) return null
  const { x, y } = scalesFor(points, height)
  return { cx: Number(fixedDecimals(x(last.date), 2)), cy: Number(fixedDecimals(y(last.cents), 2)) }
}

export function BalanceHistory({ account }: { account: Account }) {
  const [range, setRange] = useState<Range>('1M')
  const phone = useBreakpoint() === 'phone'
  const descId = useId()

  const points = useMemo(
    () => buildSeries(account.id, account.currentCents ?? 0, range),
    [account.id, account.currentCents, range],
  )
  const height = phone ? SPARK_HEIGHT : CHART_HEIGHT
  const path = useMemo(() => pathFor(points, height), [points, height])
  const end = useMemo(() => endPointFor(points, height), [points, height])
  const ticks = useMemo(() => monthTicks(points), [points])
  const latest = points[points.length - 1]
  const earliest = points[0]
  const cents = points.map((p) => p.cents)
  const low = cents.length > 0 ? Math.min(...cents) : null
  const high = cents.length > 0 ? Math.max(...cents) : null

  const summary = latest && (
    <span className="balance-history__value">
      <Money cents={latest.cents} currency={account.currency} kind="stock" />
    </span>
  )

  return (
    <div className="balance-history">
      {/* Title and range control share one row, title left, control sized
          to its content on the right — the artboard's placement (team-lead
          finding, screenshotted): `SegmentedControl` rendered as a bare
          block below the title before this, and a flex COLUMN's default
          `align-items: stretch` (`.balance-history` itself) stretched it to
          the panel's full width. Moved here from `AccountDetail.tsx`,
          which used to render this heading itself, so the row that needs
          the control beside it can own both — `.account-form__title` is
          reused rather than restated so the heading still matches its two
          siblings ("Account settings", "Transactions on this account") in
          size/weight/colour. `MerchantsToolbar.tsx:175`'s inline
          `SegmentedControl` is the in-repo precedent for constraining width
          via a flex row rather than styling the control itself. */}
      <div className="balance-history__header">
        <h3 className="account-form__title">Balance history</h3>
        {/* `M5-R30`: below 640px this degrades to a sparkline with ONE
            callout — no range switcher, no axis chrome, just the shrunk
            line and the single value a sparkline is too small to make
            legible on its own. Range stays at whatever was last chosen
            (default `1M`) rather than resetting, so switching to phone
            mid-session does not silently change what is shown. */}
        {!phone && (
          <SegmentedControl
            label="Range"
            name={`balance-history-range-${account.id}`}
            options={RANGE_OPTIONS}
            value={range}
            onChange={setRange}
            size="panel"
          />
        )}
      </div>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${height}`}
        role="img"
        // C1 (architect round 2, #396, blocking): subject before caveat --
        // the previous string led with SAMPLE_DATA_NOTE, so a screen-reader
        // user heard the disclaimer before ever learning the image WAS a
        // balance history. This exact shape is pinned by two e2e locators
        // (account-detail.spec.ts, account-detail.mobile.spec.ts) matching
        // `/showing ${range}\./i` -- team-lead specified this string
        // precisely because the architect's own suggested form drops the
        // word "Showing" and would have broken those locators a second time.
        aria-label={`Balance history, showing ${range}. ${SAMPLE_DATA_NOTE}`}
        aria-describedby={descId}
        className="balance-history__chart"
      >
        {/* N7 (architect review, #396, non-blocking): `vector-effect="non-scaling-stroke"` --
            a `stroke-width` inside a 640-unit viewBox scales with the container otherwise, so the
            SAME `2` renders ~1px on a 375px phone and ~2.8px on a wide panel. */}
        {!phone &&
          Array.from({ length: GRIDLINE_COUNT }, (_, i) => {
            const gy = PAD + ((height - 2 * PAD) * (i + 1)) / (GRIDLINE_COUNT + 1)
            return (
              <line
                key={i}
                className="balance-history__gridline"
                x1={PAD}
                x2={CHART_WIDTH - PAD}
                y1={gy}
                y2={gy}
                vectorEffect="non-scaling-stroke"
              />
            )
          })}
        <path
          d={path}
          className="balance-history__line"
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
        {end && <circle className="balance-history__end-dot" cx={end.cx} cy={end.cy} r={phone ? END_DOT_R_SPARK : END_DOT_R} />}
      </svg>
      {!phone && ticks.length > 0 && (
        <div className="balance-history__ticks">
          {ticks.map((t) => (
            <span
              key={t.key}
              className={
                t.current
                  ? 'balance-history__tick balance-history__tick--current'
                  : 'balance-history__tick'
              }
            >
              {t.label}
            </span>
          ))}
        </div>
      )}
      {/* N6 (architect review, #396, non-blocking): role="img" plus a NAME
          ("Balance history, 1M") told a screen-reader user nothing about
          the DATA -- and makes the svg's own children presentational, so a
          bare <title>/<desc> would need this same explicit reference
          anyway. A visually-hidden summary, referenced by aria-describedby
          above, carries start/end/low/high -- the same four numbers a
          sighted user reads directly off the line's shape. */}
      {earliest && latest && low !== null && high !== null && (
        <p id={descId} className="sr-only">
          {'Start '}
          <Money cents={earliest.cents} currency={account.currency} kind="stock" />
          {', end '}
          <Money cents={latest.cents} currency={account.currency} kind="stock" />
          {', low '}
          <Money cents={low} currency={account.currency} kind="stock" />
          {', high '}
          <Money cents={high} currency={account.currency} kind="stock" />
          {'.'}
        </p>
      )}
      {phone ? (
        <span className="balance-history__summary balance-history__callout">{summary}</span>
      ) : (
        <span className="balance-history__summary">{summary}</span>
      )}
      <p className="note">{SAMPLE_DATA_NOTE}</p>
    </div>
  )
}
